// Human text for the machine `reason` codes of the API: the `*_reason` that
// accompanies a null value, and the `reason` of an error body. Unknown codes
// are shown as-is: a named reason is always better than a silent dash.
// A code may carry a suffix after ":" (e.g. `price_unavailable:BTC`): the
// base code is looked up and the suffix appended.
const REASONS: Record<string, string> = {
  // null values
  deal_open: 'Az ügylet még nyitott, ezért nincs realizált eredmény.',
  deal_closed: 'Az ügylet lezárt.',
  price_unavailable: 'Nincs elérhető ár (a Candle szolgáltatás nem adott árat).',
  current_price_unavailable: 'Nincs mai ár, ezért nincs eltérés sem.',
  entry_price_unavailable: 'Nincs felvételi ár, ezért nincs eltérés sem.',
  quote_not_usd: 'Az ellenérték nem USD-stablecoin, ezért USD-ben nem számolható.',
  entry_price_zero: 'A felvételi ár nulla, ezért nincs eltérés.',
  no_buys_allocated: 'Az ügyletbe nincs vétel osztva, ezért nincs átlagár és bekerülési érték.',
  no_holding: 'Nincs készlet.',
  mixed_quote_assets: 'Az eladások különböző ellenérték-coinban történtek, nincs egyetlen átlagár.',
  no_entry_data: 'Nincs elég adat a felvételi árhoz.',
  no_deal: 'Nincs ügylethez rendelve (saját tőke), ezért nincs ehhez tartozó adat.',
  no_deal_allocation: 'Nincs ügyletbe osztva, ezért nincs árszint.',
  deal_reference_asset_missing: 'Az ügyletnek nincs referencia-coinja, ezért nincs árszint.',
  reference_asset_is_stablecoin: 'A referencia-coin stablecoin, annak nincs értelmezhető árszintje.',
  price_level_unavailable: 'Nincs gyertya-ár a felvétel percére.',
  no_borrow_events: 'Nincs a hitelhez tartozó felvételi esemény, ezért nincs felvételi árszint.',
  price_level_not_computed: 'A felvétel árszintje még nem lett lekérdezve.',
  fully_repaid: 'A felvétel teljesen vissza van fizetve, ezért nincs százalékos különbség.',
  value_at_borrow_zero: 'A felvételkori érték nulla, ezért nincs százalékos különbség.',
  no_sales_of_borrowed_asset: 'A felvett coinból még nem volt eladás.',
  no_candle_at_minute: 'Nincs gyertya-ár erre a percre.',
  no_live_price: 'Nincs élő ár.',
  candle_unavailable: 'A Candle szolgáltatás nem elérhető.',
  candle_unauthorized: 'A Candle szolgáltatás nem engedte be a MoneyFlow-t.',
  candle_bad_response: 'A Candle szolgáltatás értelmezhetetlen választ adott.',
  pair_not_found: 'A Candle nem ismeri ezt a párt.',
  accrual_pending_or_rate_unavailable: 'A kamat könyvelése még várat magára, vagy nincs Binance kamatláb.',
  interest_rate_unavailable: 'Nincs Binance kamatláb erre az időszakra, ezért nincs számolt kamat.',
  not_applicable: 'Erre a tételre nem értelmezhető.',
  no_data: 'Nincs adat.',
  no_price: 'Nincs elérhető ár.',
  no_open_loan: 'Nincs nyitott hitel.',
  // Binance candidates: skipped / problems / bot flag
  already_logged: 'Már a naplóban van.',
  candidate_not_found: 'Nincs a lekért listában (lejárt a 24 órás gyorsítótár). Kérd le újra.',
  candidate_has_issues: 'Az esemény értelmezése hiányos, ezért nem jelölhető meg.',
  previously_voided: 'Már naplózva volt, de vissza lett vonva; újra nem jelölhető.',
  allocation_for_unknown_candidate: 'Az allokáció olyan eseményre szól, amely nincs a kijelöltek között.',
  exceeds_event_quantity: 'Az allokáció meghaladja az esemény mennyiségét.',
  loan_history_has_no_client_id: 'A kölcsön-előzmény nem ad kliens-azonosítót, ezért a bot-jelzés ismeretlen.',
  not_an_order: 'Nem megbízás, ezért nincs kliens-azonosítója.',
  orders_unavailable: 'A megbízás-lista nem volt elérhető, ezért a bot-jelzés ismeretlen.',
  order_not_found: 'A kötéshez nem találtunk megbízást, ezért a bot-jelzés ismeretlen.',
  orders_window_saturated: 'A megbízás-lista csonka lehet, ezért a bot-jelzés ismeretlen.',
  orders_endpoint_error: 'A megbízás-lekérés hibás volt, ezért a bot-jelzés ismeretlen.',
  symbol_unparsed: 'A pár nem bontható coinokra.',
  side_unknown: 'Az irány (vétel/eladás) ismeretlen.',
  symbols_required: 'Nincs megadva pár, ezért a kötések nem kérdezhetők le.',
  assets_required: 'Nincs megadva coin, ezért ez a forrás nem kérdezhető le.',
  // Binance
  binance_credentials_unavailable: 'A Binance API-kulcs nem érhető el.',
  binance_unavailable: 'A Binance nem érhető el.',
  binance_rate_limited: 'A Binance korlátozta a lekérdezéseket, később próbáld újra.',
  binance_ip_banned: 'A Binance ideiglenesen kitiltotta a szerver IP-jét.',
  binance_auth_rejected: 'A Binance elutasította az API-kulcsot.',
  binance_window_saturated: 'Az időablak túl sűrű: a Binance teljes oldalt adott, a lista csonka lehetne. Szűkítsd az ablakot.',
  binance_api_error: 'A Binance hibát adott.',
  binance_error: 'A Binance-lekérés hibás volt.',
  // D46-D49: repay links, realized result, interest, manual price, price chain
  repay_link_mismatch: 'A hitel másik helyszínen vagy másik coinban van, mint a visszafizetés.',
  repay_link_voided: 'A kijelölt hitelfelvétel vissza van vonva, ahhoz nem köthető visszafizetés.',
  repay_link_not_a_borrow: 'A kijelölt esemény nem hitelfelvétel.',
  repay_link_not_a_repay: 'A kijelölt esemény nem visszafizetés, ezért nem rendelhető hitelhez.',
  repay_before_borrow: 'A visszafizetés a hitelfelvétel előtti időpontra esik, ezért nem köthető hozzá.',
  repay_links_exceed_borrow: 'A hozzárendelt mennyiség meghaladja a hitel kint lévő részét.',
  repay_links_exceed_repay: 'A hozzárendelt mennyiség meghaladja a visszafizetés tőke-részét (a kamat-rész nem rendelhető hitelhez).',
  repay_close_not_full: 'A teljes lezáráshoz minden kijelölt hitelt a kint lévő összegének teljes egészében vissza kell fizetni, és legalább egy hitelt ki kell jelölni.',
  interest_change_stale_trueup: 'A kamat nem módosítható: egy későbbi visszafizetés kamat-kiigazítása erre a visszafizetésre épül. Először a későbbi visszafizetést kell rendezni.',
  repay_link_invalid: 'A hozzárendelés érvénytelen: létező hitelfelvétel és pozitív mennyiség kell, és csak visszafizetéshez adható.',
  repay_links_for_unknown_candidate: 'A visszafizetés-hozzárendelés olyan eseményre szól, amely nincs a kijelöltek között.',
  repay_voided: 'A visszafizetés vissza lett vonva, ezért a hozzárendelései megszűntek.',
  borrow_voided: 'A hitelfelvétel vissza lett vonva, ezért a hozzá tartozó hozzárendelések megszűntek.',
  interest_quantity_invalid: 'A kamat nulla vagy pozitív lehet, nem haladhatja meg a visszafizetett mennyiséget, és csak visszafizetésnél adható meg.',
  manual_price_protected: 'Az árszintet kézzel adtad meg, ezért az újraszámítás csak kifejezett megerősítéssel írja felül.',
  price_level_invalid: 'Az árszint pozitív decimális szám legyen, és csak hitelfelvételnél vagy visszafizetésnél adható meg.',
  price_level_target_unknown: 'Az árszint célja ismeretlen (nincs ügylet vagy referencia-coin).',
  no_binance_candle: 'A Binance nyilvános adatában sincs gyertya erre a percre.',
  binance_public_unavailable: 'A Binance nyilvános ár-szolgáltatása nem érhető el.',
  binance_pair_not_found: 'A Binance nem ismeri ezt a párt.',
  binance_price_not_positive: 'A Binance nyilvános ára nem pozitív, ezért nem használható.',
  live_price_not_positive: 'Az élő ár nem pozitív, ezért nem használható.',
  borrow_price_level_unavailable: 'A felvétel árszintje nem ismert, ezért nincs realizált eredmény.',
  repay_price_level_unavailable: 'A visszafizetés percének árszintje nem ismert, ezért nincs realizált eredmény vagy kamatköltség.',
  no_links: 'Még nincs hitelhez kötött visszafizetés, ezért nincs realizált eredmény.',
  stablecoin_uses_deal_reference: 'Stablecoin visszafizetésének nincs saját ára: az árszint az ügylet referencia-coinja.',
  // D51: repay marked as pre-history ("előzmény")
  prehistory_not_a_repay: 'Csak visszafizetés jelölhető előzményként.',
  prehistory_nothing_unassigned: 'Ennek a visszafizetésnek nincs hozzárendelésre váró része, ezért nem jelölhető előzményként.',
  prehistory_not_marked: 'Ez a visszafizetés nincs előzményként megjelölve.',
  prehistory_stale_trueup: 'Az előzmény-jelölés most nem módosítható: egy későbbi visszafizetés kamat-kiigazítása erre a visszafizetésre épül. Először a későbbi visszafizetést kell rendezni.',
  // errors
  unauthenticated: 'A munkamenet lejárt, lépj be újra.',
  forbidden: 'Nincs jogosultság.',
  csrf: 'A biztonsági token érvénytelen. Frissítsd az oldalt.',
  not_found: 'Nem található.',
  method_not_allowed: 'Ez a művelet itt nem engedélyezett.',
  rate_limited: 'Túl sok kérés, próbáld később.',
  validation_error: 'A kérés hibás.',
  invalid_parameter: 'Érvénytelen paraméter.',
  invalid_sort: 'Érvénytelen rendezési mező.',
  deal_filter_conflict: 'Az „Ügylet” és az „Ügylet nélkül” szűrő együtt nem használható.',
  invalid_range: 'Az időszak eleje nem lehet a vége után.',
  window_too_large: 'Az időablak túl nagy (legfeljebb 120 nap).',
  database_unavailable: 'Az adatbázis nem érhető el.',
  internal_error: 'Belső hiba.',
  conflict: 'Ütközik a meglévő adattal.',
  open_loans_remain: 'Az ügyletnek még van nyitott hitele.',
  deal_already_closed: 'Az ügylet már le van zárva.',
  allocation_exceeds_event: 'Az allokációk összege meghaladja az esemény mennyiségét.',
  allocation_invalid: 'Az allokáció mennyisége pozitív kell legyen.',
  price_level_not_applicable: 'Árszint csak hitelfelvételnek vagy visszafizetésnek van.',
  event_voided: 'Az egyik esemény vissza van vonva.',
  event_already_voided: 'Az esemény már vissza van vonva.',
  merge_already_decided: 'Az összefésülésről már döntöttél.',
  location_archived: 'A helyszín archivált.',
  location_name_taken: 'Már van ilyen nevű helyszín.',
  name_required: 'A név kötelező.',
  candidates_not_markable: 'Ezek az események nem jelölhetők meg (nem a lekért listából valók, vagy már naplózottak).',
  snapshot_ledger_balance_missing: 'Az egyenleg-rögzítéshez nincs napló szerinti egyenleg.',
  quote_and_price_required: 'Vételhez és eladáshoz ellenérték-coin és ár kell.',
  quote_asset_invalid: 'Az ellenérték-coin érvénytelen.',
  price_invalid: 'Az ár érvénytelen.',
  quantity_invalid: 'A mennyiség érvénytelen.',
  fee_invalid: 'A díj érvénytelen.',
  fee_asset_required: 'A díjhoz coin kell.',
  counter_location_required: 'Transzferhez cél-helyszín kell.',
  counter_location_invalid: 'A cél-helyszín érvénytelen.',
  location_kind_invalid: 'Ez a helyszín erre nem használható.',
  no_loan_account: 'Ennek a helyszínnek nincs hitel-számlája.',
  asset_required: 'A coin megadása kötelező.',
  returnable_invalid: 'A „visszahozandó” jelölés ehhez a típushoz nem használható.',
  side_invalid: 'Az irány érvénytelen.',
  side_required: 'Az irány megadása kötelező.',
  source_invalid: 'A forrás érvénytelen.',
  type_invalid: 'Az esemény típusa érvénytelen.',
  login_not_configured: 'A bejelentkezés ezen a telepítésen nincs beállítva.',
  system_location_missing: 'Hiányzik egy rendszer-helyszín (üzemeltetési hiba).',
};

const STATUS_TEXT: Record<number, string> = {
  400: 'A kérés hibás.',
  401: 'A munkamenet lejárt, lépj be újra.',
  403: 'Nincs jogosultság.',
  404: 'Nem található.',
  409: 'Ütközik a meglévő adattal.',
  422: 'A megadott adatok érvénytelenek.',
  429: 'Túl sok kérés, próbáld később.',
  502: 'A kiszolgáló átmenetileg nem érhető el.',
  503: 'A kiszolgáló vagy egy külső szolgáltatás átmenetileg nem érhető el.',
};

/** True when the code has Hungarian text (exact or by its base before ":"). */
export function hasReasonText(reason: string): boolean {
  return reason in REASONS || reason.slice(0, Math.max(reason.indexOf(':'), 0)) in REASONS;
}

/**
 * Hungarian text of a machine reason code. A code with a suffix after ":" (e.g.
 * `price_unavailable:BTC`, `candidate_has_issues:side_unknown`) keeps the suffix:
 * a coin name is shown as it is, a nested reason code is translated too.
 */
export function reasonText(reason: string | null | undefined): string {
  if (!reason) return 'Ismeretlen érték (nincs megadott ok).';
  const direct = REASONS[reason];
  if (direct) return direct;
  const i = reason.indexOf(':');
  if (i > 0) {
    const base = REASONS[reason.slice(0, i)];
    if (base) {
      const rest = reason.slice(i + 1);
      const parts = rest.split(',').map((x) => (hasReasonText(x) ? reasonText(x) : x));
      return `${base} (${parts.join(' ')})`;
    }
  }
  // Never raw: an unknown code is named as a code, not shown as if it were prose.
  return `Ismeretlen ok (kód: ${reason}).`;
}

/** A candidate problem code (`symbol_unparsed`, `side_unknown`...) in Hungarian. */
export const issueText = (code: string): string => reasonText(code);

/** Why a server failed, in Hungarian. Never the server's English `detail` or a JS error message. */
export function errorText(status: number | null, reason: string | null): string {
  if (reason && hasReasonText(reason)) return reasonText(reason);
  if (status !== null && STATUS_TEXT[status]) return STATUS_TEXT[status]!;
  if (status !== null && status >= 500) return 'A kiszolgáló hibát jelzett.';
  if (status === null) return 'A kiszolgáló nem érhető el vagy a válasz nem értelmezhető.';
  return 'A kérés nem sikerült.';
}

const FIELD_LABEL: Record<string, string> = {
  quantity: 'Mennyiség', price: 'Ár', occurred_at: 'Időpont', asset: 'Coin', quote_asset: 'Ellenérték-coin', fee_quantity: 'Díj mennyisége',
  fee_asset: 'Díj coinja', location_id: 'Helyszín', counter_location_id: 'Cél-helyszín', deal: 'Ügylet', allocations: 'Allokáció', name: 'Név',
  note: 'Megjegyzés', tags: 'Címkék', interest_quantity: 'Ebből kamat', price_level: 'Ár kézzel', repay_links: 'Hozzárendelés', links: 'Hozzárendelés', borrow_event_id: 'Hitelfelvétel', force: 'Megerősítés', reference_asset: 'Referencia-coin', type: 'Típus', candidates: 'Események', deal_id: 'Ügylet', venue: 'Szolgáltató',
};

/** The field part of a 422 error (`allocations.0.quantity` -> "Allokáció · 1. · Mennyiség"). */
export function fieldLabel(loc: string[]): string {
  return loc
    .filter((x) => x !== 'body' && x !== 'query')
    .map((x) => (/^\d+$/.test(x) ? `${Number(x) + 1}.` : (FIELD_LABEL[x] ?? x)))
    .join(' · ');
}

/** Hungarian text for one pydantic validation message (the server's are English). */
export function fieldErrorText(msg: string): string {
  const m = msg.toLowerCase();
  if (/field required|missing/.test(m)) return 'Kötelező mező.';
  if (/greater than|positive/.test(m)) return 'Nullánál nagyobb értéket adj meg.';
  if (/at least|too short|empty/.test(m)) return 'Nem lehet üres.';
  if (/at most|too long|less than or equal/.test(m)) return 'Túl hosszú vagy túl nagy érték.';
  if (/timezone|datetime|date/.test(m)) return 'Érvényes, időzónával megadott időpont kell.';
  if (/float|decimal|number|numeric/.test(m)) return 'Érvényes decimális szám kell (szövegként).';
  if (/exactly one|not both|either/.test(m)) return 'Ebből a két megadási mód közül csak az egyik használható.';
  if (/extra|unexpected|not permitted|forbidden/.test(m)) return 'Ismeretlen mező.';
  return 'Érvénytelen érték.';
}

/** The valuation basis (D14) in Hungarian, built from the API's data, not from its English prose. */
export function valuationText(v: { stablecoin_parity: boolean; missing_price_assets: readonly string[] }): string {
  const base = `${v.stablecoin_parity ? 'A USD-értékek becslések: az USDT és az USDC 1:1-nek számít (hipotézis), ' : 'A USD-értékek becslések: '}a többi coin a Candle élő árával értékelt. Ár nélküli coinnál nulla helyett „—” látszik.`;
  return v.missing_price_assets.length ? `${base} Most nincs ára: ${v.missing_price_assets.join(', ')}.` : base;
}

const NOTE_SOURCE: Record<string, string> = {
  margin_trades: 'Margin kötések', margin_orders: 'Margin megbízások', borrow_repay: 'Kölcsön-előzmény', futures_transfer: 'Futures-transzfer',
  margin_transfer: 'Margin-transzfer', force_liquidation: 'Kényszerlikvidálás', deposit: 'Befizetés', withdraw: 'Kifizetés', binance: 'Binance',
};

/** One server note (`{source, skipped|degraded, symbol?}`, a part of the Binance read that was not complete) in Hungarian. */
export function candidateNoteText(n: Record<string, unknown>): string {
  const source = NOTE_SOURCE[String(n['source'] ?? 'binance')] ?? 'Binance';
  const what = n['skipped'] ?? n['degraded'] ?? n['reason'];
  const code = typeof what === 'string' ? what : null;
  const status = code?.startsWith('status_') ? code.slice('status_'.length) : null;
  const text = status !== null ? `a(z) „${status}” állapotú tétel kimaradt` : code !== null ? reasonText(code) : 'a forrás nem volt teljesen olvasható';
  const symbol = typeof n['symbol'] === 'string' && n['symbol'] ? ` (${n['symbol']})` : '';
  return `${source}${symbol}: ${text}`;
}
